import {derived, writable, get} from "svelte/store";
import {fetchVideoData, sanitySearch} from "../services/sanityService";
import {trialMachinableOnly} from "../services/configService";

export function default_params() {
  return {
    product: null,
    category: null,
    playlist: null,
    video: null,
    project: null,
    search: null,
    machinable_only: null,
  };
}

//
// Create the root store
//
export function create_store() {
  const {subscribe, set, update} = writable(default_params());

  return {
    subscribe,
    set_all: (
      product,
      category,
      playlist,
      project,
      video,
      search,
      machinableOnly
    ) =>
      set({
        product: product,
        category: category,
        playlist: playlist,
        project: project,
        video: video,
        search: search,
        machinable_only: machinableOnly,
      }),
  };
}

export const store = create_store();

// The video store stores all of the fetched videos
export function create_video_store() {
  const store = writable({
    categories: [],
    playlists: [],
    projects: [],
    videos: [],
    search: [],
  });

  function getProductFilter(productUrlStr) {
    if (productUrlStr === null) {
      return 'aspire';
    }
    switch (productUrlStr) {
      case 'cut2d_desktop':
        return "cut2dDesktop"
      case "cut2d_pro":
        return "cut2dPro"
      case 'vcarve_desktop':
        return 'vcarveDesktop';
      case 'vcarve_pro':
        return 'vcarvePro';
      case 'aspire':
        return 'aspire';
      default:
        return "aspire"
    }
  }

  /**
   * Filters the list:
   * Applies the filter fn to list.videos
   * Then decides whether this element of the list is acceptabel
   * If it is then list.videos = filtered list and this item
   * remains
   * @param{Array<{videos: Array<any>}>} list - List of object with
   * @param{ function(any): boolean} filterFn- List of object with
   * @param{function(filteredList: Array<any> originalList: Array<any>): boolean} acceptanceFn - List of object with
   */
  function filterByVideos(list, filterFn, acceptanceFn) {
    const filteredList = [];
    for (let i = 0; i < list.length; i++) {
      let filteredVideos = list[i].videos.filter(filterFn);
      if (acceptanceFn(filteredVideos, list[i].videos)) {
        const filteredPlaylist = Object.assign({}, list[i])
        filteredPlaylist.videos = filteredVideos;
        filteredList.push(filteredPlaylist);
      }
    }
    return filteredList
  }

  function set({categories, playlists, projects, videos, search, product}) {


    /* Filter playlists, categories and projects by software */
    let theProduct = getProductFilter(product)

    const filterFn = (v) => {
      return (v[theProduct] === true) && (!trialMachinableOnly() || v.trialCompatible)};

    const filteredVideos = videos.filter(filterFn);

    const filteredPlaylists = filterByVideos(playlists, filterFn, (l) => l.length > 0)
    const filteredCategories = filterByVideos(categories, filterFn, l => l.length > 0)
    const filteredProjects = filterByVideos(projects, filterFn,
      (filtered, orginal) => {
      return (filtered.length === orginal.length && filtered.length > 1)
    })

    store.set({
      categories: filteredCategories,
      playlists: filteredPlaylists,
      projects: filteredProjects,
      videos: filteredVideos,
      search,
    });
  }

  return {
    subscribe: store.subscribe,
    set,
  };
}

export const video_store = create_video_store();

/**
 * Returns a normalized  video store so that we can easily look up something by slug.current
 * @type {Readable<{projects: {}, playlists: {}, videos: {}, categories: {}}>}
 */
export function create_normalized_store(videoStore) {
  return derived(video_store,
    ($videoStore) => {
      const result = {
        videos: {},
        playlists: {},
        categories: {},
        projects: {},
      };

      $videoStore.videos.map((v) => {
        result.videos[v.slug.current] = v;
      });

      $videoStore.categories.map((cat) => {
        result.categories[cat.slug.current] = cat;
      });

      $videoStore.playlists.map((playlist) => {
        result.playlists[playlist.slug.current] = playlist;
      });

      $videoStore.projects
        .filter((project) => project.videos && project.videos.length > 1)
        .map((project) => {
          result.projects[project.slug.current] = project;
        });

      return result;
    });
}

export const normalized_video_store = create_normalized_store(video_store);

//
// The derived video store reads the filter state
// and the video store and returns the filtered data
// it is read only, and only reflects what
// the filters and the video show
//

function create_active_store(store, video_store) {
  const activeStore = derived(
    [store, video_store],
    ([$store, $video_store]) => {
      // check if we have a playlist active. If we do then we filter the videos
      // to reflect that

      const result = {
        filtered_videos: $video_store.videos,
        active_playlist: null,
        active_video: null,
        active_category: null,
        active_project: null,
        active_search: null,
      };

      if ($store.playlist !== null) {
        const matching_playlist = $video_store.playlists.find(
          (p) => p.slug.current === $store.playlist
        );
        if (matching_playlist) {
          result.active_playlist = matching_playlist;
          result.filtered_videos = matching_playlist.videos.map((v) => v);

          if ($store.category !== null) {
            const matching_category = $video_store.categories.find(
              (c) => c.slug.current === $store.category
            );
          }
        }
      }

      if ($store.category !== null) {
        const matching_category = $video_store.categories.find(
          (c) => c.slug.current === $store.category
        );

        if (matching_category) {
          result.active_category = matching_category;
          const matching_category_id = $video_store.categories.find(
            (c) => c._id === matching_category._id
          );
          if (matching_category_id) {
            result.active_category_id = matching_category_id._id;
            const filteredvideos = [];
            let thevideos = $video_store.videos;
            for (let i = 0; i < thevideos.length; i++) {
              if (thevideos[i].category) {
                if (thevideos[i].category.find(cat => cat.category._ref === result.active_category_id)) {
                  filteredvideos.push(thevideos[i]);
                }
              }
            }
            result.filtered_videos = filteredvideos;
          }
        }
      }

      if ($store.project !== null) {
        const matching_project = $video_store.projects.find(
          (p) => p.slug.current === $store.project
        );
        if (matching_project) {
          result.active_project = matching_project;
          const someFilteredVideos = [];
          let thevideos = $video_store.videos;
          for (let i = 0; i < matching_project.videos.length; i++) {
            let videoid = matching_project.videos[i]._id;
            for (let i = 0; i < thevideos.length; i++) {
              if (thevideos[i]._id === videoid) {
                someFilteredVideos.push(thevideos[i]);
              }
            }
          }
          result.filtered_videos = someFilteredVideos;
        }
      }

      if ($store.video !== null) {
        const matching_video = $video_store.videos.find(
          (v) => v.slug.current === $store.video
        );
        if (matching_video) {
          result.active_video = matching_video;
        } else {
          console.warn(`Unable to find ${$store.video} in  ${$video_store.videos.map(v => v.slug.current)}`)
        }
      }
      return result;
    }
  );

  return activeStore;
}

export const active_video_store = create_active_store(store, video_store);


function createSearchStore() {
  const {subscribe, update, set} = writable({
    searching: false,
    results: [],
  });

  const reset = () => {
    set({searching: false, results: []});
  };

  return {
    subscribe,
    update,
    set,
    reset,
  };
}

export const search_store = createSearchStore();

store.subscribe(async ($store) => {
  if (!$store.search || $store.search === "") {
    search_store.reset();
    return;
  }
  try {
    search_store.update((u) => {
      u.searching = true;
      return u;
    });
    const results = await sanitySearch($store.search);
    search_store.set({searching: false, results});
  } catch (e) {
    search_store.reset();
  }
});

export const sidebarToggled = writable(false);
